import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'

import type { GetToken } from '~/config'
import { createClient } from '~/data/graphql'
import { messageText } from '~/data/messageText'
import { deleteNote, updateNote, type NoteLite } from '~/data/notes'
import { theme } from '~/theme'

// One note, in full: markdown editing with a rendered preview. Notes written
// in the dashboard's rich editor arrive as HTML — those preview as clean text
// (via messageText) and edit as raw source.

/** Minimal, safe markdown → HTML: escape EVERYTHING first, then re-introduce
 *  a small whitelist of tags from markdown syntax only. */
function markdownHtml(src: string): string {
  // Quotes MUST be escaped too: the link rule interpolates the URL into an
  // href="..." attribute, and an unescaped quote would break out of it.
  const escaped = src
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
  const lines = escaped.split('\n').map((line) => {
    let l = line
    const h = /^(#{1,3})\s+(.*)$/.exec(l)
    if (h) {
      const level = h[1].length
      const size = level === 1 ? 15 : level === 2 ? 13.5 : 12.5
      return `<div style="font-weight:700;font-size:${size}px;margin:8px 0 3px;color:#efe9dc">${h[2]}</div>`
    }
    if (/^\s*[-*]\s+/.test(l)) l = l.replace(/^\s*[-*]\s+/, '•&nbsp;')
    l = l
      .replace(/\*\*(.+?)\*\*/g, '<b>$1</b>')
      .replace(/`([^`]+)`/g, '<code style="background:#0b0d12;border-radius:4px;padding:0 4px">$1</code>')
      .replace(/\[([^\]]+)\]\((https?:\/\/[^)\s"'<>&]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer" style="color:#c98a6b">$1</a>')
    return l || '<br/>'
  })
  return lines.join('<br/>').replace(/(<br\/>){3,}/g, '<br/><br/>')
}

export function NoteDetailPanel({
  note,
  pulseId,
  organizationId,
  getToken,
  onClose,
}: {
  note: NoteLite
  pulseId: string
  organizationId: string
  getToken: GetToken
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const isHtml = /<[a-z][^>]*>/i.test(note.content ?? '')
  const [title, setTitle] = useState(note.title)
  const [content, setContent] = useState(note.content ?? '')
  // Fresh empty notes open straight into editing.
  const [editing, setEditing] = useState(!note.content)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [note2, setNote2] = useState<string | null>(null)
  const client = async () => createClient(await getToken())

  const refresh = () =>
    void queryClient.invalidateQueries({ queryKey: ['roomNotes', pulseId] })

  const save = useMutation({
    mutationFn: async () => {
      await updateNote(await client(), {
        organizationId,
        pulseId,
        noteId: note.id,
        title: title.trim() || note.title,
        // Empty string, NOT null: the API's UpdateNoteMutation drops nulls as
        // "unchanged", so clearing a note's body would silently not persist.
        content,
      })
    },
    onSuccess: () => {
      setNote2('Saved ✓')
      setTimeout(() => setNote2(null), 2000)
      setEditing(false)
      refresh()
    },
    onError: (e) =>
      setNote2(e instanceof Error ? e.message.slice(0, 120) : 'Save failed'),
  })

  const remove = useMutation({
    mutationFn: async () => deleteNote(await client(), note.id),
    onSuccess: () => {
      refresh()
      onClose()
    },
    onError: (e) =>
      setNote2(e instanceof Error ? e.message.slice(0, 120) : 'Delete failed'),
  })

  const preview = useMemo(() => {
    if (!content.trim()) return null
    if (isHtml) return { text: messageText(content) }
    return { html: markdownHtml(content) }
  }, [content, isHtml])

  return (
    <div style={backdrop} onClick={onClose}>
      <div style={card} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 15 }}>{note.pinned ? '⭐' : '📄'}</span>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            style={{ ...titleInput, flex: 1 }}
          />
          <button onClick={onClose} style={closeBtn} aria-label="Close">×</button>
        </div>

        <div style={{ display: 'flex', gap: 6, margin: '10px 0 8px' }}>
          <button
            onClick={() => setEditing(false)}
            style={{ ...tabBtn, ...(editing ? null : tabActive) }}
          >
            Preview
          </button>
          <button
            onClick={() => setEditing(true)}
            style={{ ...tabBtn, ...(editing ? tabActive : null) }}
          >
            Edit
          </button>
          <span style={{ flex: 1 }} />
          {confirmDelete ? (
            <>
              <button onClick={() => remove.mutate()} style={{ ...tabBtn, color: '#ff8a7a', borderColor: '#5a2a26' }}>
                Really delete
              </button>
              <button onClick={() => setConfirmDelete(false)} style={tabBtn}>
                Keep
              </button>
            </>
          ) : (
            <button onClick={() => setConfirmDelete(true)} style={{ ...tabBtn, color: theme.textDim }}>
              Delete
            </button>
          )}
        </div>

        {editing ? (
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={'Write in markdown…\n\n# Heading\n- bullet\n**bold** `code` [link](https://…)'}
            rows={14}
            autoFocus
            style={editor}
          />
        ) : preview ? (
          'html' in preview ? (
            <div
              style={previewBox}
              dangerouslySetInnerHTML={{ __html: preview.html ?? '' }}
            />
          ) : (
            <div style={{ ...previewBox, whiteSpace: 'pre-wrap' }}>{preview.text}</div>
          )
        ) : (
          <div style={{ ...previewBox, color: theme.textDim }}>
            Empty note — switch to Edit to write it.
          </div>
        )}

        {editing && isHtml && (
          <div style={{ fontSize: 10, color: theme.textDim, marginTop: 4 }}>
            This note was written in the dashboard's rich editor — you're editing its raw source.
          </div>
        )}

        <button
          onClick={() => save.mutate()}
          disabled={save.isPending || !title.trim()}
          style={saveBtn}
        >
          {save.isPending ? 'Saving…' : 'Save note'}
        </button>
        {note2 && (
          <div
            style={{
              fontSize: 11,
              marginTop: 6,
              textAlign: 'center',
              color: note2 === 'Saved ✓' ? '#54d18c' : '#ff8a7a',
            }}
          >
            {note2}
          </div>
        )}
      </div>
    </div>
  )
}

const backdrop: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  background: '#140d07c9',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
}
const card: React.CSSProperties = {
  width: 'min(520px, 94vw)',
  maxHeight: '86vh',
  overflowY: 'auto',
  padding: 16,
  background: theme.panel,
  border: `1px solid ${theme.border}`,
  borderRadius: 14,
  boxShadow: '0 24px 80px #000c',
  color: theme.text,
}
const titleInput: React.CSSProperties = {
  background: 'none',
  border: 'none',
  borderBottom: `1px solid ${theme.border}`,
  color: theme.cream,
  fontSize: 15,
  fontWeight: 700,
  padding: '4px 2px',
  outline: 'none',
}
const closeBtn: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: theme.textDim,
  cursor: 'pointer',
  fontSize: 20,
  lineHeight: 1,
  padding: '2px 6px',
}
const tabBtn: React.CSSProperties = {
  background: 'none',
  border: `1px solid ${theme.border}`,
  color: theme.textDim,
  borderRadius: 8,
  fontSize: 11,
  padding: '4px 11px',
  cursor: 'pointer',
}
const tabActive: React.CSSProperties = {
  color: theme.cream,
  background: '#232633',
}
const editor: React.CSSProperties = {
  display: 'block',
  width: '100%',
  background: '#0e1016',
  border: `1px solid ${theme.border}`,
  color: theme.text,
  borderRadius: 9,
  fontSize: 12.5,
  fontFamily: 'ui-monospace, monospace',
  lineHeight: 1.6,
  padding: 12,
  resize: 'vertical',
  outline: 'none',
}
const previewBox: React.CSSProperties = {
  minHeight: 120,
  background: '#0e1016',
  border: `1px solid ${theme.border}`,
  borderRadius: 9,
  fontSize: 12.5,
  lineHeight: 1.65,
  padding: 12,
  overflowWrap: 'anywhere',
}
const saveBtn: React.CSSProperties = {
  display: 'block',
  width: '100%',
  marginTop: 12,
  background: theme.terracotta,
  border: 'none',
  color: '#fff',
  borderRadius: 9,
  fontSize: 13,
  fontWeight: 700,
  padding: '10px 0',
  cursor: 'pointer',
}
